<template>
  <v-expansion-panel @change="validate()">
    <v-expansion-panel-header>Ontario</v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form ref="form">
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-tooltip bottom  max-width="300" nudge-top="20" open-delay="1000">
            <template v-slot:activator="{on}">
              <v-switch v-model="ontario.accrueInQBO">
                <span slot="label" v-on="on">Accrue Ontario tax and LCBO fees liability in QuickBooks Online</span>
              </v-switch>
            </template>
            When selected, PŪRTRAK will automatically credit the LCBO Liability and Ontario Ministry of Finance accounts
            on duty-paid retail and licensee sales, debit the LCBO Commissions account on licensee sales, and
            debit the promotional expense account for samples.
          </v-tooltip>
          <account-picker v-model="ontario.mofPayableAccountRef" label="Ministry of Finance liability account"
                          :accounts="liabilityAccounts" :addTypes="['OtherCurrentLiability']"
                          :disabled="!ontario.accrueInQBO"
                          class="mb-3" hint="To accrue liabilities to the Ontario MOF"/>
          <account-picker v-model="ontario.lcboPayableAccountRef" label="LCBO liability account"
                          :accounts="liabilityAccounts" :addTypes="['OtherCurrentLiability']"
                          :disabled="!ontario.accrueInQBO"
                          class="mb-3" hint="To accrue liabilities to the LCBO"/>
          <account-picker v-model="ontario.hstPayableAccountRef" label="HST liability account"
                          :accounts="liabilityAccounts" :addTypes="['OtherCurrentLiability']"
                          :disabled="!ontario.accrueInQBO"
                          class="mb-3" hint="To accrue HST liabilities"/>
          <account-picker v-model="ontario.ontarioTaxesExpenseAccountRef" label="Taxes expense account"
                          :accounts="expenseAccounts" :addTypes="['Expense','OtherExpense','CostOfGoodsSold','Income']"
                          :disabled="!ontario.accrueInQBO"
                          class="mb-3" hint="To track taxes, markups, etc."/>
        </template>

        <v-tooltip bottom max-width="300" nudge-top="20" open-delay="1000">
          <template v-slot:activator="{on}">
            <v-switch v-model="ontario.createReceipt" @change="validate()">
              <span slot="label" v-on="on">Create inventory receipt for payments</span>
            </v-switch>
          </template>
          When selected, filing a MOF Spirits Return or LCBO Distillery Report report will create an Invenetory Receipt transaction for any payment due.
        </v-tooltip>

        <v-autocomplete v-model="ontario.mofPaymentToId" outlined dense label="MOF payee" :rules="[fieldRules.required]"
                        :items="suppliers" item-text="name" item-value="id" no-data-text="No suppliers found"
                        :disabled="!ontario.createReceipt">
          <template v-slot:prepend-item>
            <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer"
                @click="paymentToId='mofPaymentToId';paymentToDefaultName='Minister of Finance';supplierCreate=true">
              <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new supplier
            </div>
          </template>
        </v-autocomplete>
        <v-autocomplete v-model="ontario.lcboPaymentToId" outlined dense label="LCBO payee" :rules="[fieldRules.required]"
                        :items="suppliers" item-text="name" item-value="id" no-data-text="No suppliers found"
                        :disabled="!ontario.createReceipt">
          <template v-slot:prepend-item>
            <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer"
                @click="paymentToId='lcboPaymentToId';paymentToDefaultName='LCBO Accounts Receivable';supplierCreate=true">
              <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new supplier
            </div>
          </template>
        </v-autocomplete>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-autocomplete label="QuickBooks payment method" v-model="ontario.paymentMethodId"
                          :items="paymentMethods" item-value="id" item-text="name" outlined dense @change="validate()"
                          :disabled="!ontario.createReceipt"/>

          <account-picker v-model="ontario.paymentAccountRef" :rules="[fieldRules.required]"
                          label="Payment account" class="mb-3"
                          :accounts="paymentAccounts"
                          :disabled="!ontario.paymentMethodId || !ontario.createReceipt"
                          required/>
        </template>
        <v-autocomplete label="Payment item for MOF remittance" v-model="ontario.mofPaymentItemId" :rules="[fieldRules.required]"
                        :items="items" item-value="id" item-text="name" outlined dense class="mt-7"
                        :disabled="!ontario.createReceipt">
          <template v-slot:prepend-item>
            <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer"
                  @click="createDefaulItem('Ontario Spirits Return remittance', 'mofPaymentItemId', 'mofPayableAccountRef')">
              <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Create default MOF payment item
            </div>
          </template>
        </v-autocomplete>
        <v-autocomplete label="Payment item for LCBO remittance" v-model="ontario.lcboPaymentItemId" :rules="[fieldRules.required]"
                        :items="items" item-value="id" item-text="name" outlined dense
                        :disabled="!ontario.createReceipt">
          <template v-slot:prepend-item>
            <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer"
                  @click="createDefaulItem('LCBO Distillery Reporting remittance','lcboPaymentItemId', 'lcboPayableAccountRef')">
              <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Create default LCBO payment item
            </div>
          </template>
        </v-autocomplete>

        <v-tooltip bottom  max-width="300" open-delay="1000" nudge-top="20">
          <template v-slot:activator="{on}">
            <div v-on="on">
              <decimal-number-input label="LCBO freight rate" v-model="ontario.freightRate" suffix="per case" v-on="on" style="width:150px" class="mt-3"/>
            </div>
          </template>
          The current case freight rate charged by the LCBO.
          Used to calculate shelf price of bottles from case price.
        </v-tooltip>

        <v-tooltip bottom  max-width="300" open-delay="1000" nudge-top="20">
          <template v-slot:activator="{on}">
            <div v-on="on">
              <v-switch v-model="ontario.lcboDirectDeliveryFromDutyPaid" label="LCBO duty-paid sales from duty-paid inventory"/>
            </div>
          </template>
          By default, LCBO duty-paid sales (usually used for LCBO direct delivery) will pull product from in-bond inventory.
          Turn this option on to have the default sales pull from duty-paid inventory instead.
        </v-tooltip>

        <v-tooltip bottom  max-width="300" open-delay="1000" nudge-top="20">
          <template v-slot:activator="{on}">
            <div v-on="on">
              <v-switch v-model="ontario.lcboQuarterly">
                <span slot="label" v-on="on">LCBO quarterly reporting</span>
              </v-switch>
            </div>
          </template>
          By default, sales reports are due to the LCBO monthly.
          As of April 1, 2022, qualified distilleries can now report quarterly.
        </v-tooltip>
      </v-form>
      <supplier-quick-create v-model="supplierCreate" @create="newSupplier" :defaultName="paymentToDefaultName"/>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import settingsPreferences from '@/views/settings/preferences/settingsPreferences'
import SupplierQuickCreate from '@/views/purchasing/suppliers/SupplierQuickCreate'

export default {
  name: 'ConfigurationSettingsON',
  mixins: [settingsPreferences],
  components: {SupplierQuickCreate},
  data() {
    return {
      ontario: {},
      suppliers: [],
      paymentAccounts: [],
      liabilityAccounts: [],
      expenseAccounts: [],
      paymentMethods: [{id:'',name:'Do not make a QuickBooks payment'},{divider:true}],
      items: [],
      supplierCreate:false,
      paymentToId: '',
      paymentToDefaultName: '',
      companyAccounts: {}
    }
  },
  async created() {
    this.$lazyListLoad(this.suppliers, 'vendors2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.items, 'items2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'},{field:'type',comparator:'ne',value:'Item'}]);
    this.$lazyListLoad(this.paymentMethods, 'paymentMethods2', 'id,name', 100);
    const results = await this.$apiCall(`query ConfigurationSettingsON {
                                            ontarioPreferences {
                                              accrueInQBO,freightRate,lcboQuarterly,lcboDirectDeliveryFromDutyPaid
                                              mofPayableAccountRef,lcboPayableAccountRef,hstPayableAccountRef,ontarioTaxesExpenseAccountRef
                                              lcboPaymentToId,mofPaymentToId,paymentMethodId,paymentAccountRef
                                              mofPaymentItemId,lcboPaymentItemId
                                              createReceipt
                                            }
                                            accounts {
                                              ref,name,type,typeName,currency
                                            }
                                            companyAccounts {
                                              serviceUsage,serviceIncome,
                                            }
                                          }`,
                                        {});
    if (!results.error) {
      this.ontario = results.data.ontarioPreferences;
      this.paymentAccounts = results.data.accounts.filter(account =>
        account.currency === this.$store.getters.company.settings.homeCurrency &&
        (account.type === 'Bank' || account.type === 'CreditCard' || account.type === 'OtherCurrentAsset')
      )
      this.liabilityAccounts = results.data.accounts.filter(account => account.type === 'OtherCurrentLiability');
      this.expenseAccounts = results.data.accounts.filter(account => account.type === 'Expense' || account.type === 'OtherExpense' || account.type === 'CostOfGoodsSold' || account.type === 'Income');
      this.companyAccounts = results.data.companyAccounts;
    }
  },
  watch: {
    ontario: {
      async handler() {
        await this.$apiCall(`mutation ConfigurationSettingsON($input:OntarioPreferencesInput!) {
                              ontarioSetPreferences(input:$input) {
                                accrueInQBO
                              }
                            }`,
                            {input:this.ontario});
      },
      immediate: false,
      deep: true
    }
  },
  methods: {
    validate() {
      this.$nextTick(() => this.$refs.form.validate());
    },
    async newSupplier(supplier) {
      this.suppliers.push(supplier);
      this.ontario[this.paymentToId] = supplier.id;
    },
    async createDefaulItem(name, itemId, liabilityAccount) {
      const input = {type:'Service', name, sku:'',active:true,
                     location:'',lotTracking:false,trackUnits:'',precision:0,usageCost:0,
                     allowNegativeInventory:false,reorderPoint:0,
                     inventoryAccountRef:this.companyAccounts.serviceUsage,
                     incomeAccountRef:this.companyAccounts.serviceIncome,
                     cogAccountRef:this.ontario[liabilityAccount],
                     buyingUnits:'',buyingPrecision:0,convertToTracking:1,
                     weight:0,upc:'',reorderQuantity:0,reorderDays:0,reorderBusinessDays:false,
                     buyingDescription:'',sold:false,sellingDescription:'',defaultPrice:0,commissionExempt:false,
                     assembled:false,bom:[],customers:[],vendors:[],initialAsof:'2021-01-01 00:00:00'};
      const results = await this.$apiCall(`mutation ConfigurationSettingsON($input: ItemInput!) {
                                             itemCreate(input:$input) {id,name,type,trackUnits,precision,category{id},active,sold}
                                           }`, {input});
      if (!results.error) {
        this.$store.commit('itemAdd', results.data.itemCreate);
        this.items.push(results.data.itemCreate);
        this.ontario[itemId] = results.data.itemCreate.id;
      }
    }
  }
}
</script>